import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { Separator } from '@/components/shadcn/separator'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { notify } from '@/lib/notify'
import {
  usePostApiCompaniesCurrentGroups,
  usePatchApiCompaniesCurrentGroupsGroupId,
  useGetApiCompaniesCurrentActionmodules,
  useGetApiCompaniesCurrentShopmodules,
  useGetApiCompaniesCurrentEmployees,
  useGetApiCompaniesCurrentGroupsGroupIdMembers,
  getGetApiCompaniesCurrentGroupsGroupIdMembersQueryKey,
} from '@/api/generated/company-management-user/company-management-user'
import type { ExtendedGroupResponse } from '@/api/model/extendedGroupResponse'
import type { ShopModuleType } from '@/api/model/shopModuleType'
import { shopModuleLabel, ShopModuleTypeIcon } from './ShopModuleIcon'
import { ActionModuleTypeIcon } from './ActionModuleIcon'
import { SelectColumn } from './SelectColumn'
import { ColorPicker } from '@/components/common/ColorPicker'

interface GroupDialogProps {
  open: boolean
  onClose: () => void
  editing?: ExtendedGroupResponse | null
}

export function GroupDialog({ open, onClose, editing }: GroupDialogProps) {
  const { t } = useTranslation(['groups', 'common'])
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [colorHex, setColorHex] = useState(editing?.colorHex ?? "#43B8BB")
  const [selectedActionIds, setSelectedActionIds] = useState<number[]>([])
  const [selectedShopIds, setSelectedShopIds] = useState<number[]>([])
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { mutateAsync: create } = usePostApiCompaniesCurrentGroups()
  const { mutateAsync: update } = usePatchApiCompaniesCurrentGroupsGroupId()

  const { data: actionData } = useGetApiCompaniesCurrentActionmodules()
  const { data: shopData } = useGetApiCompaniesCurrentShopmodules()
  const { data: employeeData } = useGetApiCompaniesCurrentEmployees()
  const { data: membersData } = useGetApiCompaniesCurrentGroupsGroupIdMembers(
    editing?.id ?? 0, {},
    { query: { enabled: !!editing?.id } },
  )

  const actionModules = ((actionData as any)?.data ?? actionData ?? []) as {
    id?: number; name: string | null; iconUrl?: string | null; iconName?: string | null; colorHex?: string | null
  }[]
  const shopModules = ((shopData as any)?.data ?? shopData ?? []) as { id: number; type: ShopModuleType }[]
  const employees = ((employeeData as any)?.data ?? employeeData ?? []) as {
    id: string | null;
    firstName: string | null;
    lastName: string | null
  }[]

  useEffect(() => {
    if (open) {
      setName(editing?.name ?? '')
      setSelectedActionIds((editing?.actionModuleIds ?? []) as number[])
      setSelectedShopIds((editing?.shopModuleIds ?? []) as number[])
    }
  }, [open, editing])

  useEffect(() => {
    if (membersData) {
      const members = ((membersData as any)?.data ?? membersData ?? []) as { id: string | null }[]
      setSelectedMemberIds(members.map((m) => m.id).filter(Boolean) as string[])
    }
  }, [membersData])

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['/api/companies/current/taxbenefitgroups'] })

  const handleSubmit = async () => {
    if (!name.trim()) return
    setIsSubmitting(true)
    try {
      const payload = {
        name,
        colorHex,
        actionModuleIds: selectedActionIds,
        shopModuleIds: selectedShopIds,
        memberIds: selectedMemberIds,
      }
      if (editing) {
        await update({ groupId: editing.id, data: payload })
        await queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentGroupsGroupIdMembersQueryKey(editing.id),
        })
        notify.success(t('toasts.updated'))
      } else {
        await create({ data: payload })
        notify.success(t('toasts.created'))
      }
      await invalidate()
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.saveError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl!">
        <DialogHeader>
          <DialogTitle>{editing ? t('dialog.titleEdit') : t('dialog.titleCreate')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          <div className='flex flex-row gap-5 items-end'>
            <div className="flex flex-col gap-1.5 w-75">
              <Label htmlFor="group-name">{t('dialog.name.label')} <span className="text-destructive">*</span></Label>
              <Input
                id="group-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('dialog.name.placeholder')}
              />
            </div>
            <ColorPicker
              className='pb-1'
              value={colorHex ?? ""} onChange={setColorHex}></ColorPicker>
          </div>

          <Separator />

          <div className="grid grid-cols-3 gap-4">
            <SelectColumn
              label={t('dialog.actionModules.label')}
              searchLabel={t('dialog.actionModules.searchLabel')}
              items={actionModules
                .filter((a) => a.id != null)
                .map((a) => ({
                  id: a.id!,
                  label: a.name ?? `${t('dialog.actionModules.fallbackName')} ${a.id}`,
                  icon: <ActionModuleTypeIcon module={a} />,
                }))}
              selectedIds={selectedActionIds}
              onChange={(ids) => setSelectedActionIds(ids as number[])}
              emptyText={t('dialog.actionModules.empty')}
            />

            <SelectColumn
              label={t('dialog.shopModules.label')}
              searchLabel={t('dialog.shopModules.searchLabel')}
              items={shopModules.map((s) => ({
                id: s.id,
                label: shopModuleLabel(s.type),
                icon: <ShopModuleTypeIcon type={s.type} className="size-4 text-foreground" />,
              }))}
              selectedIds={selectedShopIds}
              onChange={(ids) => setSelectedShopIds(ids as number[])}
              emptyText={t('dialog.shopModules.empty')}
            />

            <SelectColumn
              label={t('dialog.members.label')}
              searchLabel={t('dialog.members.searchLabel')}
              items={employees
                .filter((e) => e.id)
                .map((e) => ({
                  id: e.id!,
                  label: [e.firstName, e.lastName].filter(Boolean).join(' ') || e.id!,
                }))}
              selectedIds={selectedMemberIds}
              onChange={(ids) => setSelectedMemberIds(ids as string[])}
              emptyText={t('dialog.members.empty')}
              selectAllLabel={t('dialog.members.selectAll')}
            />
          </div>
        </div>

        <DialogFooter className="flex-row items-center gap-2">
          <Button variant="outline" onClick={onClose}
            disabled={isSubmitting}>{t('buttons.cancel', { ns: 'common' })}</Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || isSubmitting}>
            {isSubmitting ? t('buttons.saving', { ns: 'common' }) : t('buttons.save', { ns: 'common' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
